/**
 * Community Goal API
 *
 * Mock service for community goals and group challenges.
 */

import type {
  CommunityGoal,
  GoalContribution,
  GoalParticipation,
  GoalLeaderboardEntry,
  ApiResponse,
  UUID,
} from '../types'
import { mockDb, delay, timestamp } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Community Goal API
 */
export const communityGoalApi = {
  /**
   * Create a new community goal
   */
  async createGoal(communityId: UUID, data: {
    title: string
    description: string
    contributionType: CommunityGoal['contributionType']
    targetAmount: number
    startDate: string
    endDate: string
    rewardXp: number
  }): Promise<ApiResponse<CommunityGoal>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const goal = mockDb.insert<CommunityGoal>('communityGoals', {
      communityId,
      title: data.title,
      description: data.description,
      contributionType: data.contributionType,
      targetAmount: data.targetAmount,
      currentAmount: 0,
      startDate: data.startDate,
      endDate: data.endDate,
      status: new Date(data.startDate) <= new Date() ? 'active' : 'upcoming',
      milestones: [],
      participantCount: 0,
      rewardXp: data.rewardXp,
    })

    return success(goal)
  },

  /**
   * Get all goals for a community
   */
  async getGoals(communityId: UUID): Promise<ApiResponse<CommunityGoal[]>> {
    await delay(100)

    const goals = mockDb.findWhere<CommunityGoal>(
      'communityGoals',
      g => g.communityId === communityId
    )

    // Sort by start date
    goals.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())

    return success(goals)
  },

  /**
   * Get a single goal by ID
   */
  async getGoalById(goalId: UUID): Promise<ApiResponse<CommunityGoal>> {
    await delay(50)

    const goal = mockDb.findById<CommunityGoal>('communityGoals', goalId)
    if (!goal) {
      return error('Goal not found')
    }

    return success(goal)
  },

  /**
   * Get active goals (across all communities user is member of)
   */
  async getActiveGoals(): Promise<ApiResponse<CommunityGoal[]>> {
    await delay(100)

    const now = new Date()
    const goals = mockDb.findWhere<CommunityGoal>(
      'communityGoals',
      g => g.status === 'active' && new Date(g.endDate) > now
    )

    return success(goals)
  },

  /**
   * Join a community goal
   */
  async joinGoal(goalId: UUID): Promise<ApiResponse<GoalParticipation>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const goal = mockDb.findById<CommunityGoal>('communityGoals', goalId)
    if (!goal) {
      return error('Goal not found')
    }

    // Check if already participating
    const existing = mockDb.findWhere<GoalParticipation>(
      'goalParticipations',
      p => p.goalId === goalId && p.userId === userId
    )
    if (existing.length > 0) {
      return error('Already participating in this goal')
    }

    const participation = mockDb.insert<GoalParticipation>('goalParticipations', {
      goalId,
      userId,
      joinedAt: timestamp(),
      personalContribution: 0,
    })

    // Update participant count
    mockDb.update<CommunityGoal>('communityGoals', goalId, {
      participantCount: goal.participantCount + 1,
    })

    return success(participation)
  },

  /**
   * Add a contribution to a goal
   */
  async contribute(
    goalId: UUID,
    amount: number,
    challengeId?: UUID,
    trackRecordId?: UUID
  ): Promise<ApiResponse<GoalContribution>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const goal = mockDb.findById<CommunityGoal>('communityGoals', goalId)
    if (!goal) {
      return error('Goal not found')
    }

    if (goal.status !== 'active') {
      return error('Goal is not active')
    }

    // Get user name
    const user = mockDb.findById<{ displayName: string }>('users', userId)

    const contribution = mockDb.insert<GoalContribution>('goalContributions', {
      goalId,
      userId,
      contributorName: user?.displayName || 'Anonymous',
      amount,
      contributionType: goal.contributionType,
      challengeId,
      trackRecordId,
      contributedAt: timestamp(),
    })

    // Update goal progress
    const newAmount = goal.currentAmount + amount
    const updates: Partial<CommunityGoal> = { currentAmount: newAmount }

    // Check milestones
    const updatedMilestones = goal.milestones.map(m => {
      if (!m.reached && newAmount >= m.targetAmount) {
        return { ...m, reached: true, reachedAt: timestamp() }
      }
      return m
    })
    updates.milestones = updatedMilestones

    // Check if goal is completed
    if (newAmount >= goal.targetAmount) {
      updates.status = 'completed'
    }

    mockDb.update<CommunityGoal>('communityGoals', goalId, updates)

    // Update personal participation
    const participations = mockDb.findWhere<GoalParticipation>(
      'goalParticipations',
      p => p.goalId === goalId && p.userId === userId
    )
    if (participations.length > 0) {
      mockDb.update<GoalParticipation>('goalParticipations', participations[0].goalId, {
        personalContribution: participations[0].personalContribution + amount,
      })
    }

    return success(contribution)
  },

  /**
   * Get leaderboard for a goal
   */
  async getLeaderboard(goalId: UUID, limit = 10): Promise<ApiResponse<GoalLeaderboardEntry[]>> {
    await delay(100)

    const contributions = mockDb.findWhere<GoalContribution>(
      'goalContributions',
      c => c.goalId === goalId
    )

    // Aggregate by user
    const userTotals = new Map<string, { name: string; total: number }>()
    contributions.forEach(c => {
      const existing = userTotals.get(c.userId)
      if (existing) {
        existing.total += c.amount
      } else {
        userTotals.set(c.userId, { name: c.contributorName, total: c.amount })
      }
    })

    // Sort and rank
    const sorted = Array.from(userTotals.entries())
      .map(([userId, { name, total }]) => ({ userId, displayName: name, contribution: total }))
      .sort((a, b) => b.contribution - a.contribution)
      .slice(0, limit)
      .map((entry, index) => ({ ...entry, rank: index + 1 }))

    return success(sorted)
  },

  /**
   * Get user's participation in a goal
   */
  async getMyParticipation(goalId: UUID): Promise<ApiResponse<GoalParticipation | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return success(null)
    }

    const participations = mockDb.findWhere<GoalParticipation>(
      'goalParticipations',
      p => p.goalId === goalId && p.userId === userId
    )

    return success(participations[0] || null)
  },

  /**
   * Get recent contributions for a goal
   */
  async getRecentContributions(
    goalId: UUID,
    limit = 20
  ): Promise<ApiResponse<GoalContribution[]>> {
    await delay(100)

    const contributions = mockDb.findWhere<GoalContribution>(
      'goalContributions',
      c => c.goalId === goalId
    )

    // Sort by date descending
    contributions.sort(
      (a, b) => new Date(b.contributedAt).getTime() - new Date(a.contributedAt).getTime()
    )

    return success(contributions.slice(0, limit))
  },
}
